import { z } from 'zod';

const optionalStringList = z.array(z.string()).nullable().optional();
const optionalTimestamp = z.number().nullable().optional();

export const XpanseNetworkRecordSchema = z.object({
  handle: z.string().nullable().optional(),
  organizationName: z.string().nullable().optional(),
  organizationRegistryName: z.string().nullable().optional(),
  asnRegistryName: z.string().nullable().optional(),
  asnNumber: z.number().nullable().optional(),
  firstIp: z.string().nullable().optional(),
  lastIp: z.string().nullable().optional(),
  remarks: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
});
export type XpanseNetworkRecord = z.infer<typeof XpanseNetworkRecordSchema>;

export const XpanseIpRangeSchema = z.object({
  range_id: z.string(),
  first_ip: z.string().nullable().optional(),
  last_ip: z.string().nullable().optional(),
  date_added: optionalTimestamp,
  organization_handles: optionalStringList,
  business_units: optionalStringList,
  tags: optionalStringList,
  details: z
    .object({
      networkRecords: z.array(XpanseNetworkRecordSchema).nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type XpanseIpRange = z.infer<typeof XpanseIpRangeSchema>;

// Xpanse keeps adding asset_type members, so this stays a free-form string and
// is classified at ingest rather than validated here.
export const XPANSE_DOMAIN_ASSET_TYPES = ['DOMAIN', 'CERTIFICATE'] as const;

export const XpanseInternetExposureAssetSchema = z.object({
  asset_id: z.string().nullable().optional(),
  asm_ids: optionalStringList,
  name: z.string().nullable().optional(),
  sanitized_asset_name: z.string().nullable().optional(),
  asset_type: z.string().nullable().optional(),
  domain: z.string().nullable().optional(),
  ips: optionalStringList,
  cloud_provider: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  resolves: z.boolean().nullable().optional(),
  has_active_external_services: z.boolean().nullable().optional(),
  externally_detected_providers: optionalStringList,
  externally_inferred_vulnerability_score: z.number().nullable().optional(),
  certificate_issuer: z.string().nullable().optional(),
  certificate_algorithm: z.string().nullable().optional(),
  business_units: optionalStringList,
  tags: optionalStringList,
  first_observed: optionalTimestamp,
  last_observed: optionalTimestamp,
});
export type XpanseInternetExposureAsset = z.infer<
  typeof XpanseInternetExposureAssetSchema
>;

// Xpanse returns inferred CVEs as bare ids on some tenants and as objects on
// others; both shapes normalize to a cve id at ingest.
export const XpanseInferredCveSchema = z.union([
  z.string(),
  z.object({
    cve_id: z.string().nullable().optional(),
    cvss_score_v3: z.number().nullable().optional(),
    cvss_score_v2: z.number().nullable().optional(),
    inferred_cve_match_type: z.string().nullable().optional(),
    product: z.string().nullable().optional(),
    vendor: z.string().nullable().optional(),
    version: z.string().nullable().optional(),
  }),
]);
export type XpanseInferredCve = z.infer<typeof XpanseInferredCveSchema>;

export const XpanseTlsVersionSchema = z.object({
  tls_version: z.string().nullable().optional(),
  cipher_suite: z.string().nullable().optional(),
});
export type XpanseTlsVersion = z.infer<typeof XpanseTlsVersionSchema>;

export const XpanseExternalServiceSchema = z.object({
  service_id: z.string(),
  service_name: z.string().nullable().optional(),
  service_type: z.string().nullable().optional(),
  ip_address: optionalStringList,
  domain: optionalStringList,
  port: z.number().nullable().optional(),
  protocol: z.string().nullable().optional(),
  is_active: z.string().nullable().optional(),
  discovery_type: z.string().nullable().optional(),
  externally_detected_providers: optionalStringList,
  externally_inferred_cves: z
    .array(XpanseInferredCveSchema)
    .nullable()
    .optional(),
  confirmed_vulnerable_cve_ids: optionalStringList,
  active_classifications: optionalStringList,
  inactive_classifications: optionalStringList,
  tls_versions: z.array(XpanseTlsVersionSchema).nullable().optional(),
  business_units: optionalStringList,
  first_observed: optionalTimestamp,
  last_observed: optionalTimestamp,
  details: z
    .object({
      tlsVersions: z.array(XpanseTlsVersionSchema).nullable().optional(),
      serviceKey: z.string().nullable().optional(),
      serviceKeyType: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type XpanseExternalService = z.infer<typeof XpanseExternalServiceSchema>;

export const isXpanseServiceActive = ({
  service,
}: {
  service: Pick<XpanseExternalService, 'is_active'>;
}): boolean => service.is_active?.toLowerCase() !== 'inactive';
